<template>
  <!-- Install Prompt Banner -->
  <Transition name="slide-down">
    <div
      v-if="showInstallBanner && isInstallable && !isStandalone"
      class="pwa-banner pwa-banner--install"
    >
      <div class="pwa-banner__icon">
        <v-icon size="24" color="primary">mdi-cellphone-arrow-down</v-icon>
      </div>
      <div class="pwa-banner__content">
        <div class="pwa-banner__title">Install App</div>
        <div class="pwa-banner__text">Add to your home screen for the best experience</div>
      </div>
      <div class="pwa-banner__actions">
        <v-btn variant="text" size="small" @click="dismissInstall">
          Later
        </v-btn>
        <v-btn color="primary" size="small" @click="handleInstall">
          Install
        </v-btn>
      </div>
    </div>
  </Transition>

  <!-- Update Available Banner -->
  <Transition name="slide-up">
    <div
      v-if="shouldShowUpdateBanner"
      class="pwa-banner pwa-banner--update"
    >
      <div class="pwa-banner__icon">
        <v-icon size="24" color="success">mdi-update</v-icon>
      </div>
      <div class="pwa-banner__content">
        <div class="pwa-banner__title">Update Available</div>
        <div class="pwa-banner__text">A new version is ready to install</div>
      </div>
      <div class="pwa-banner__actions">
        <v-btn variant="text" size="small" @click="dismissUpdate">
          Later
        </v-btn>
        <v-btn color="success" size="small" @click="handleUpdate">
          Update Now
        </v-btn>
      </div>
    </div>
  </Transition>

  <!-- Offline Indicator -->
  <Transition name="slide-down">
    <div
      v-if="!isOnline"
      class="pwa-offline-indicator"
    >
      <v-icon size="16" color="warning">mdi-wifi-off</v-icon>
      <span>You're offline</span>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { usePWA } from '@/composables/usePWA'

const route = useRoute()
const {
  needRefresh,
  isInstallable,
  isStandalone,
  isOnline,
  installApp,
  updateApp,
  dismissUpdate,
} = usePWA()

const showInstallBanner = ref(false)
const hasCompletedActions = ref(false)

// Only show update banner in production (not in dev environment)
const shouldShowUpdateBanner = computed(() => {
  return needRefresh.value && !import.meta.env.DEV
})

// Check if on login page - never show install banner there
const isLoginPage = computed(() => route.path === '/login')

// Check if user dismissed install banner recently
const DISMISS_KEY = 'pwa_install_dismissed'
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000 // 7 days

// Session tracking for delay
const SESSION_START_KEY = 'pwa_session_start'
const SHOW_DELAY = 2 * 60 * 1000 // 2 minutes

// User engagement tracking
const USER_ACTIONS_KEY = 'pwa_user_actions'
const REQUIRED_ACTIONS = 3 // User must complete at least 3 actions

let delayTimer: number | null = null

function checkDismissed() {
  const dismissed = localStorage.getItem(DISMISS_KEY)
  if (dismissed) {
    const dismissedAt = parseInt(dismissed, 10)
    if (Date.now() - dismissedAt < DISMISS_DURATION) {
      return false
    }
  }
  return true
}

function checkUserEngagement(): boolean {
  const actions = parseInt(localStorage.getItem(USER_ACTIONS_KEY) || '0', 10)
  return actions >= REQUIRED_ACTIONS
}

function trackUserAction() {
  const current = parseInt(localStorage.getItem(USER_ACTIONS_KEY) || '0', 10)
  const newCount = current + 1
  localStorage.setItem(USER_ACTIONS_KEY, newCount.toString())

  if (newCount >= REQUIRED_ACTIONS) {
    hasCompletedActions.value = true
  }
}

function initializeDelayTimer() {
  // Don't show if user dismissed recently
  if (!checkDismissed()) {
    return
  }

  // Check if user has completed enough actions
  hasCompletedActions.value = checkUserEngagement()
  if (!hasCompletedActions.value) {
    // Set up global event listeners to track user engagement
    setupEngagementTracking()
    return
  }

  // Check if this is a new session or continuing one
  const sessionStart = sessionStorage.getItem(SESSION_START_KEY)
  const now = Date.now()

  if (!sessionStart) {
    // New session - record start time
    sessionStorage.setItem(SESSION_START_KEY, now.toString())

    // Set timer to show banner after delay
    delayTimer = window.setTimeout(() => {
      if (!isLoginPage.value) {
        showInstallBanner.value = true
      }
    }, SHOW_DELAY)
  } else {
    // Existing session - check if delay has elapsed
    const elapsed = now - parseInt(sessionStart, 10)

    if (elapsed >= SHOW_DELAY) {
      // Delay already passed, show immediately (but not on login)
      if (!isLoginPage.value) {
        showInstallBanner.value = true
      }
    } else {
      // Set timer for remaining time
      const remaining = SHOW_DELAY - elapsed
      delayTimer = window.setTimeout(() => {
        if (!isLoginPage.value) {
          showInstallBanner.value = true
        }
      }, remaining)
    }
  }
}

// Track meaningful user interactions
let engagementListenersAttached = false
function setupEngagementTracking() {
  if (engagementListenersAttached) return
  engagementListenersAttached = true

  // Track navigation (route changes)
  watch(() => route.path, () => {
    trackUserAction()
    checkAndShowBanner()
  })

  // Track clicks on interactive elements
  const handleClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement
    // Only count clicks on buttons, links, or cards
    if (target.closest('button, a, .v-card, .post-card, [role="button"]')) {
      trackUserAction()
      checkAndShowBanner()
    }
  }
  document.addEventListener('click', handleClick)

  // Track scroll engagement
  let scrollCount = 0
  const handleScroll = () => {
    scrollCount++
    // Count every 5 scroll events as one action
    if (scrollCount % 5 === 0) {
      trackUserAction()
      checkAndShowBanner()
    }
  }
  window.addEventListener('scroll', handleScroll, { passive: true })
}

function checkAndShowBanner() {
  if (hasCompletedActions.value && checkDismissed() && !isLoginPage.value) {
    // User has now completed enough actions, start the delay timer
    const sessionStart = sessionStorage.getItem(SESSION_START_KEY)
    if (!sessionStart) {
      sessionStorage.setItem(SESSION_START_KEY, Date.now().toString())
    }

    const elapsed = Date.now() - parseInt(sessionStorage.getItem(SESSION_START_KEY) || Date.now().toString(), 10)
    if (elapsed >= SHOW_DELAY) {
      showInstallBanner.value = true
    } else if (!delayTimer) {
      delayTimer = window.setTimeout(() => {
        if (!isLoginPage.value) {
          showInstallBanner.value = true
        }
      }, SHOW_DELAY - elapsed)
    }
  }
}

// Hide banner when navigating to login page
watch(isLoginPage, (onLogin) => {
  if (onLogin) {
    showInstallBanner.value = false
  }
})

onMounted(() => {
  initializeDelayTimer()
})

onUnmounted(() => {
  if (delayTimer !== null) {
    clearTimeout(delayTimer)
  }
})

async function handleInstall() {
  const installed = await installApp()
  if (!installed) {
    // User dismissed, hide for a while
    dismissInstall()
  }
}

function dismissInstall() {
  showInstallBanner.value = false
  localStorage.setItem(DISMISS_KEY, Date.now().toString())
}

async function handleUpdate() {
  await updateApp()
}
</script>

<style lang="scss" scoped>
.pwa-banner {
  position: fixed;
  top: 35%; // Position in upper third to avoid audio player overlap
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.24);
  z-index: 1500;
  max-width: 400px;
  width: calc(100% - 32px);

  &__icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(var(--v-theme-primary), 0.1);
    border-radius: 10px;
  }

  &--update &__icon {
    background: rgba(var(--v-theme-success), 0.1);
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-weight: 600;
    font-size: 0.875rem;
    color: rgb(var(--v-theme-on-surface));
  }

  &__text {
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.7);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }
}

.pwa-offline-indicator {
  position: fixed;
  top: 64px; // Below header
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(var(--v-theme-warning), 0.15);
  border: 1px solid rgba(var(--v-theme-warning), 0.3);
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  color: rgb(var(--v-theme-warning));
  z-index: 1000;
}

// Transitions
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.95);
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.95);
}

// Responsive - mobile layout adjustments
@media (max-width: 480px) {
  .pwa-banner {
    flex-wrap: wrap;

    &__content {
      flex-basis: calc(100% - 52px);
    }

    &__actions {
      width: 100%;
      justify-content: flex-end;
      margin-top: 8px;
    }
  }
}
</style>
